<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — per-account model access.
 * Account-level tenant narrowing of the Eva model picker: the admin chooses which of the models the
 * account is ENTITLED to (the operator-owned ceiling in custom_attributes) to actually offer in the
 * assistant picker. Tenant can only narrow, never widen — the backend intersects the saved selection
 * with the entitlement (CHA-67). "Offer all" is persisted as an empty list so a later operator grant
 * is auto-offered. The card renders only when there is a real choice (more than one entitled model),
 * so the common single-model install shows nothing. Tailwind-only; self-contained for vitest.
 */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaAccountSettingsAPI from 'dashboard/api/eva/accountSettings';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Let the parent refresh any open assistant model picker once the offered set changes.
const emit = defineEmits(['saved']);

const { t } = useI18n();

const entitled = ref([]); // operator ceiling (read-only options)
const selected = ref([]); // models currently offered — always an explicit set in the UI
const isSaving = ref(false);
const status = ref(''); // '' | 'saved' | 'error'

// Only worth showing when the admin has more than one model to choose between.
const hasChoice = computed(() => entitled.value.length > 1);
const noneChecked = computed(() => selected.value.length === 0);

const load = async () => {
  try {
    const { data } = await EvaAccountSettingsAPI.get();
    entitled.value = data.eva_entitled_models || [];
    // Server stores [] to mean "offer all"; materialise it into an explicit all-checked set for the UI.
    const stored = (data.eva_selected_models || []).filter(model =>
      entitled.value.includes(model)
    );
    selected.value = stored.length ? stored : [...entitled.value];
  } catch {
    status.value = 'error';
  }
};

const toggle = model => {
  selected.value = selected.value.includes(model)
    ? selected.value.filter(entry => entry !== model)
    : [...selected.value, model];
  status.value = '';
};

const save = async () => {
  isSaving.value = true;
  status.value = '';
  // All checked → persist [] so newly-granted models are auto-offered; else the explicit subset.
  const allChecked = entitled.value.every(model =>
    selected.value.includes(model)
  );
  try {
    await EvaAccountSettingsAPI.update({
      eva_selected_models: allChecked ? [] : selected.value,
    });
    status.value = 'saved';
    emit('saved'); // an open assistant picker must reload /eva/models to drop a just-removed model
  } catch {
    status.value = 'error';
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);

defineExpose({ load, toggle, save });
</script>

<template>
  <div class="contents">
    <section
      v-if="hasChoice"
      class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <div class="flex flex-col gap-1">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('EVA_MODEL_ACCESS.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('EVA_MODEL_ACCESS.HINT') }}
        </p>
      </div>

      <ul class="flex flex-col gap-2 max-w-md list-none">
        <li v-for="model in entitled" :key="model">
          <label
            class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
          >
            <Checkbox
              :model-value="selected.includes(model)"
              @change="toggle(model)"
            />
            <span class="font-mono">{{ model }}</span>
          </label>
        </li>
      </ul>

      <div class="flex items-center gap-3">
        <Button
          :label="t('EVA_MODEL_ACCESS.SAVE')"
          sm
          :is-loading="isSaving"
          :disabled="isSaving || noneChecked"
          @click="save"
        />
        <p v-if="noneChecked" class="text-xs text-n-amber-11">
          {{ t('EVA_MODEL_ACCESS.NONE_HINT') }}
        </p>
        <p v-else-if="status === 'saved'" class="text-sm text-n-teal-11">
          {{ t('EVA_MODEL_ACCESS.SAVED') }}
        </p>
        <p v-else-if="status === 'error'" class="text-sm text-n-ruby-11">
          {{ t('EVA_MODEL_ACCESS.ERROR') }}
        </p>
      </div>
    </section>
  </div>
</template>
